import { useState, useEffect, useCallback } from 'react';
import { useFormacao, type Formacao, type EditarFormacaoInput } from '@/context/FormacaoContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { useCategoriasFormacao } from '@/hooks/useCategoriasFormacao';

interface Props {
  formacao: Formacao;
  onClose: () => void;
  onSuccess?: (updated: Formacao) => void;
}

const MODALIDADES = ['Presencial', 'E-Learning', 'Misto (Blended)', 'Videoconferência'];
const ESTADOS = ['Planeada', 'Em Curso', 'Concluída', 'Cancelada'];

type FormState = {
  nome: string;
  entidade_formadora: string;
  categoria: string;
  modalidade: string;
  estado: string;
  data_inicio: string;
  data_fim: string;
  duracao_horas: number;
  local: string;
  financiamento: string;
  descricao: string;
  observacoes: string;
  formador_interno_id: string;
};

export default function EditarFormacaoModal({ formacao, onClose, onSuccess }: Props) {
  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);
  const { editarFormacao, saving, formadores } = useFormacao();
  const [form, setForm] = useState<FormState>({
    nome: formacao.nome ?? '',
    entidade_formadora: formacao.entidade_formadora ?? '',
    categoria: formacao.categoria ?? '',
    modalidade: formacao.modalidade ?? 'Presencial',
    estado: formacao.estado ?? 'Planeada',
    data_inicio: formacao.data_inicio ?? '',
    data_fim: formacao.data_fim ?? '',
    duracao_horas: formacao.duracao_horas ?? 0,
    local: formacao.local ?? '',
    financiamento: formacao.financiamento ?? '',
    descricao: formacao.descricao ?? '',
    observacoes: formacao.observacoes ?? '',
    formador_interno_id: formacao.formador_interno_id ?? '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [activeTab, setActiveTab] = useState<'info' | 'datas' | 'financeiro'>('info');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { categorias: CATEGORIAS } = useCategoriasFormacao();

  useEffect(() => {
    setForm({
      nome: formacao.nome ?? '',
      entidade_formadora: formacao.entidade_formadora ?? '',
      categoria: formacao.categoria ?? '',
      modalidade: formacao.modalidade ?? 'Presencial',
      estado: formacao.estado ?? 'Planeada',
      data_inicio: formacao.data_inicio ?? '',
      data_fim: formacao.data_fim ?? '',
      duracao_horas: formacao.duracao_horas ?? 0,
      local: formacao.local ?? '',
      financiamento: formacao.financiamento ?? '',
      descricao: formacao.descricao ?? '',
      observacoes: formacao.observacoes ?? '',
      formador_interno_id: formacao.formador_interno_id ?? '',
    });
  }, [formacao]);

  function set<K extends keyof FormState>(key: K, val: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate(): boolean {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.nome.trim()) errs.nome = 'O título é obrigatório';
    if (!form.entidade_formadora.trim()) errs.entidade_formadora = 'A entidade é obrigatória';
    if (!form.categoria) errs.categoria = 'Escolha uma categoria';
    if (!form.data_inicio) errs.data_inicio = 'Data de início obrigatória';
    if (!form.data_fim) errs.data_fim = 'Data de fim obrigatória';
    if (form.data_inicio && form.data_fim && form.data_fim < form.data_inicio) {
      errs.data_fim = 'A data de fim não pode ser anterior à de início';
    }
    if (!form.duracao_horas || form.duracao_horas <= 0) errs.duracao_horas = 'Insira a duração em horas';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;
    const duracao = parseFloat(String(form.duracao_horas)) || 0;
    const payload: EditarFormacaoInput = {
      nome: form.nome.trim(),
      entidade_formadora: form.entidade_formadora.trim() || null,
      categoria: form.categoria || null,
      modalidade: form.modalidade,
      estado: form.estado,
      data_inicio: form.data_inicio || null,
      data_fim: form.data_fim || null,
      duracao_horas: duracao,
      local: form.local.trim() || null,
      custo: 0,
      financiamento: form.financiamento.trim() || null,
      descricao: form.descricao.trim() || null,
      observacoes: form.observacoes.trim() || null,
      formador_interno_id: form.formador_interno_id || null,
    };
    try {
      const ok = await editarFormacao(formacao.id, payload);
      if (ok) {
        const updated: Formacao = {
          ...formacao,
          ...payload,
          duracao_horas: duracao,
          custo: 0,
        };
        onSuccess?.(updated);
        handleClose();
      } else {
        setSubmitError('Não foi possível guardar as alterações. Tente novamente.');
      }
    } catch {
      setSubmitError('Erro inesperado. Tente novamente.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, formacao, editarFormacao, onSuccess, handleClose]);

  const estadoCores: Record<string, string> = {
    'Planeada': 'text-zinc-600 bg-zinc-100 border-zinc-200',
    'Em Curso': 'text-amber-700 bg-amber-50 border-amber-200',
    'Concluída': 'text-emerald-700 bg-emerald-50 border-emerald-200',
    'Cancelada': 'text-rose-600 bg-rose-50 border-rose-200',
  };

  const tabs = [
    { id: 'info' as const, label: 'Informação', icon: 'ri-information-line' },
    { id: 'datas' as const, label: 'Datas & Local', icon: 'ri-calendar-line' },
  ];

  const formadorSelecionado = formadores.find((f) => f.id === form.formador_interno_id);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-auto mx-4 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-start justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 flex-shrink-0 mt-0.5">
              <i className="ri-edit-line text-white text-base" />
            </div>
            <div className="min-w-0">
              <h2 className="text-[16px] font-bold text-zinc-900">Editar Ação de Formação</h2>
              <p className="text-[11px] text-zinc-400 truncate max-w-[340px]">{formacao.nome}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Estado rápido (sempre visível) */}
        <div className="px-7 pt-4 pb-3 flex items-center gap-3 border-b border-zinc-50">
          <span className="text-[12px] font-semibold text-zinc-500">Estado:</span>
          <div className="flex items-center gap-2 flex-wrap">
            {ESTADOS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => set('estado', s)}
                className={`whitespace-nowrap text-[12px] font-semibold px-3 py-1 rounded-full border transition-all cursor-pointer ${
                  form.estado === s
                    ? estadoCores[s] ?? 'text-zinc-600 bg-zinc-100 border-zinc-200'
                    : 'text-zinc-400 bg-white border-zinc-100 hover:border-zinc-300'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-7 pt-4 flex items-center gap-1 border-b border-zinc-100">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-lg transition-all cursor-pointer mb-[-1px] ${
                activeTab === t.id
                  ? 'text-zinc-900 bg-zinc-50 border-b-2 border-zinc-900'
                  : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${t.icon} text-sm`} />
              </div>
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-5 space-y-4">
          {/* Tab: Informação */}
          {activeTab === 'info' && (
            <>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                  Título da Ação <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => set('nome', e.target.value)}
                  className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors.nome ? 'border-rose-400' : 'border-zinc-200'}`}
                />
                {errors.nome && <p className="text-[11px] text-rose-500 mt-1">{errors.nome}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                    Entidade Formadora <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.entidade_formadora}
                    onChange={(e) => set('entidade_formadora', e.target.value)}
                    className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors.entidade_formadora ? 'border-rose-400' : 'border-zinc-200'}`}
                  />
                  {errors.entidade_formadora && <p className="text-[11px] text-rose-500 mt-1">{errors.entidade_formadora}</p>}
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                    Categoria / Área <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={form.categoria}
                    onChange={(e) => set('categoria', e.target.value)}
                    className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${errors.categoria ? 'border-rose-400' : 'border-zinc-200'}`}
                  >
                    <option value="">Selecionar área...</option>
                    {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {errors.categoria && <p className="text-[11px] text-rose-500 mt-1">{errors.categoria}</p>}
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Modalidade</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {MODALIDADES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => set('modalidade', m)}
                      className={`whitespace-nowrap text-[12px] font-medium px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${
                        form.modalidade === m
                          ? 'bg-zinc-900 text-white border-zinc-900'
                          : 'text-zinc-500 bg-white border-zinc-200 hover:border-zinc-400'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Formador Interno */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-star-line text-sm text-green-600" />
                    </div>
                    Formador Interno
                    <span className="text-[10px] font-normal text-zinc-400 ml-1">opcional</span>
                  </span>
                </label>
                {formadores.length === 0 ? (
                  <div className="flex items-center gap-2 px-3.5 py-2.5 border border-dashed border-zinc-200 rounded-lg bg-zinc-50">
                    <div className="w-4 h-4 flex items-center justify-center text-zinc-400">
                      <i className="ri-information-line text-sm" />
                    </div>
                    <p className="text-[12px] text-zinc-400">Nenhum formador interno registado. Adicione em <span className="font-semibold">Formadores Internos</span>.</p>
                  </div>
                ) : (
                  <>
                    <select
                      value={form.formador_interno_id}
                      onChange={(e) => set('formador_interno_id', e.target.value)}
                      className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
                    >
                      <option value="">— Sem formador interno —</option>
                      {formadores.map((f) => (
                        <option key={f.id} value={f.id}>{f.nome}</option>
                      ))}
                    </select>
                    {formadorSelecionado && (
                      <div className="mt-1.5 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-100 rounded-lg">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-green-100 flex-shrink-0">
                          <i className="ri-user-star-line text-xs text-green-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[12px] font-semibold text-green-800">{formadorSelecionado.nome}</p>
                          {formadorSelecionado.areas_formacao && formadorSelecionado.areas_formacao.length > 0 && (
                            <p className="text-[10px] text-green-600 truncate">{formadorSelecionado.areas_formacao.join(', ')}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Descrição</label>
                <textarea
                  rows={3}
                  value={form.descricao}
                  onChange={(e) => set('descricao', e.target.value)}
                  maxLength={500}
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
                />
                <p className="text-[10px] text-zinc-400 text-right mt-0.5">{form.descricao.length}/500</p>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Observações</label>
                <textarea
                  rows={2}
                  value={form.observacoes}
                  onChange={(e) => set('observacoes', e.target.value)}
                  maxLength={500}
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
                />
              </div>
            </>
          )}

          {/* Tab: Datas & Local */}
          {activeTab === 'datas' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                    Data de Início <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.data_inicio}
                    onChange={(e) => set('data_inicio', e.target.value)}
                    className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${errors.data_inicio ? 'border-rose-400' : 'border-zinc-200'}`}
                  />
                  {errors.data_inicio && <p className="text-[11px] text-rose-500 mt-1">{errors.data_inicio}</p>}
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                    Data de Fim <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.data_fim}
                    onChange={(e) => set('data_fim', e.target.value)}
                    className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer ${errors.data_fim ? 'border-rose-400' : 'border-zinc-200'}`}
                  />
                  {errors.data_fim && <p className="text-[11px] text-rose-500 mt-1">{errors.data_fim}</p>}
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                    Duração (horas) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={form.duracao_horas === 0 ? '' : form.duracao_horas}
                    onChange={(e) => set('duracao_horas', Number(e.target.value))}
                    placeholder="Ex: 14"
                    className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors.duracao_horas ? 'border-rose-400' : 'border-zinc-200'}`}
                  />
                  {errors.duracao_horas && <p className="text-[11px] text-rose-500 mt-1">{errors.duracao_horas}</p>}
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Local</label>
                <input
                  type="text"
                  value={form.local}
                  onChange={(e) => set('local', e.target.value)}
                  placeholder="Ex: Sala de Formação A, Online..."
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                />
              </div>

              {/* Resumo visual do intervalo */}
              {form.data_inicio && form.data_fim && (
                <div className="bg-zinc-50 rounded-xl p-4">
                  <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">Resumo</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-[13px] font-semibold text-zinc-700">
                      {new Date(form.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </span>
                    <div className="w-8 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-zinc-300" />
                    </div>
                    <span className="text-[13px] font-semibold text-zinc-700">
                      {new Date(form.data_fim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </span>
                    {form.duracao_horas > 0 && (
                      <span className="ml-auto text-[13px] font-bold text-zinc-900">{form.duracao_horas}h</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Erro de submissão */}
          {submitError && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-600 text-[12px] px-3 py-2 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-sm" />
              </div>
              {submitError}
            </div>
          )}
          {/* Footer */}
          <div className="flex items-center justify-between gap-2 pt-3 border-t border-zinc-100">
            <div className="flex items-center gap-2">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className={`whitespace-nowrap w-2 h-2 rounded-full transition-all cursor-pointer ${activeTab === t.id ? 'bg-zinc-900 w-5' : 'bg-zinc-200 hover:bg-zinc-400'}`}
                  title={t.label}
                  aria-label={t.label}
                />
              ))}
            </div>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={handleClose}
                className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>
                    A guardar...
                  </>
                ) : (
                  <>
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line text-sm" /></div>
                    Guardar Alterações
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
